/**
 * RoamerPlannerModule.ts — Module #5: the Roamer Planner™.
 *
 * A platform module (NOT a core engine): the strategic planning center —
 * travel plans, lifestyle planning, goals, a living timeline, smart
 * suggestions, decision support, and future-scenario comparison. Not a
 * calendar. Module pattern rules:
 *  • Registers with Roamer Core™ as an EngineAdapter via attachToCore().
 *  • Inbound ONLY via Core bus events + manual entry:
 *    - LocationChanged ({ location: {...} } wrapped, flat fallback) →
 *      current base; if it matches the next destination the plan advances.
 *    - PaymentReceived → auto-progress for income/savings goals.
 *    - MissionCompleted → auto-progress for mission goals (count-based).
 *  • Outbound: the Core bus event-name union is fixed and modules must not
 *    modify Core, so — following the platform precedent (Passport
 *    achievements) — planner events publish as NotificationSent with a
 *    `kind` field: planCreated, planUpdated, goalCompleted,
 *    destinationChanged, milestoneReached, travelScheduled.
 *  • Honesty: suggestions/verdicts carry an explicit basis; thin data yields
 *    'insufficientData', never fake confidence; scenario metrics are
 *    user/engine-recorded and nullable — nothing invented. The timeline is
 *    computed at read time so milestones auto-update.
 *  • AI-assisted planning (Roamer AI Concierge™) is a registry interface
 *    only — no implementation, per the brief.
 *  • Persistence nomad:planner:v1 with record-by-record sanitization, caps,
 *    deep clones at read boundaries; export + delete supported.
 */

import { roamerCore, type CoreEvent, type EngineAdapter } from '@/services/core';
import { storageManager } from '@/services/storage/StorageManager';
import {
  DEFAULT_LIFESTYLE_PLAN,
  PLANNER_GOAL_TYPES,
  type DecisionSupport,
  type FutureComparison,
  type FutureScenario,
  type LifestylePlan,
  type PlannedDestination,
  type PlannerAiAssistant,
  type PlannerExport,
  type PlannerGoal,
  type PlannerGoalType,
  type PlannerQuestion,
  type PlannerSuggestion,
  type PlannerTimeline,
  type TravelPlanState,
} from './models';
import { computeSuggestions } from './suggestions';
import { computeTimeline } from './timeline';
import { answerQuestion } from './decisions';

const STORAGE_KEY = 'nomad:planner:v1';
const MODULE_ID = 'roamer-planner-module';
const MAX_DESTINATIONS = 100;
const MAX_GOALS = 200;
const MAX_SCENARIOS = 50;

function asString(v: unknown): string | null {
  return typeof v === 'string' && v.trim().length > 0 ? v.trim() : null;
}

function asNumberOrNull(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

// ── AI assistant registry (future — interfaces only) ─────────────────────────

const aiAssistants = new Map<string, PlannerAiAssistant>();

export function registerPlannerAiAssistant(a: PlannerAiAssistant): boolean {
  if (aiAssistants.has(a.id)) return false;
  aiAssistants.set(a.id, a);
  return true;
}

export function getPlannerAiAssistants(): PlannerAiAssistant[] {
  return Array.from(aiAssistants.values());
}

interface PersistedState {
  currentBaseCity: string | null;
  currentBaseCountry: string | null;
  destinations: PlannedDestination[];
  lifestyle: LifestylePlan;
  goals: PlannerGoal[];
  scenarios: FutureScenario[];
}

export type NewDestinationInput = {
  city: string;
  country: string;
  status: PlannedDestination['status'];
  plannedArrival?: number | null;
  plannedDeparture?: number | null;
  flexibleDates?: boolean;
  notes?: string;
};

export class RoamerPlannerModule {
  private currentBaseCity: string | null = null;
  private currentBaseCountry: string | null = null;
  private destinations: PlannedDestination[] = [];
  private lifestyle: LifestylePlan = { ...DEFAULT_LIFESTYLE_PLAN };
  private goals: PlannerGoal[] = [];
  private scenarios: FutureScenario[] = [];
  private unsubscribes: (() => void)[] = [];
  private core: typeof roamerCore | null = null;
  private listeners = new Set<() => void>();

  constructor(now: number = Date.now()) {
    this.load(now);
  }

  // ── Core integration ────────────────────────────────────────────────────────

  buildAdapter(): EngineAdapter {
    return {
      id: MODULE_ID,
      name: 'Roamer Planner™',
      version: '1.0.0',
      capabilities: ['travel-planning', 'goal-planning', 'timeline', 'decision-support', 'scenario-comparison'],
      healthCheck: async () => true,
    };
  }

  attachToCore(core: typeof roamerCore = roamerCore, now: number = Date.now()): void {
    this.detachFromCore();
    core.registerEngine(this.buildAdapter(), now);
    const safe = (handler: (e: CoreEvent) => void) => (e: CoreEvent) => {
      try {
        handler(e);
      } catch {
        /* a bad payload must never break the bus */
      }
    };
    this.unsubscribes.push(core.on('LocationChanged', safe((e) => this.onLocationChanged(e)), 'module:planner'));
    this.unsubscribes.push(core.on('PaymentReceived', safe((e) => this.onPaymentReceived(e)), 'module:planner'));
    this.unsubscribes.push(core.on('MissionCompleted', safe((e) => this.onMissionCompleted(e)), 'module:planner'));
    this.core = core;
  }

  detachFromCore(): void {
    for (const u of this.unsubscribes) u();
    this.unsubscribes = [];
    this.core = null;
  }

  /** Read-only: whether the module is currently attached to Roamer Core. */
  isAttached(): boolean {
    return this.core != null;
  }

  private publishPlannerEvent(kind: string, payload: Record<string, unknown>, at: number): void {
    try {
      this.core?.publish('NotificationSent', MODULE_ID, { kind, ...payload }, at);
    } catch {
      /* announcements are best-effort */
    }
  }

  private onLocationChanged(e: CoreEvent): void {
    const raw = (e.payload ?? {}) as Record<string, unknown>;
    const p = (raw.location != null && typeof raw.location === 'object'
      ? raw.location
      : raw) as Record<string, unknown>;
    const city = asString(p.city);
    const country = asString(p.country);
    if (!city) return;
    if (city === this.currentBaseCity) return;
    this.currentBaseCity = city;
    this.currentBaseCountry = country;
    // Arriving at the recorded next destination advances the plan.
    const next = this.destinations.find((d) => d.status === 'next');
    if (next && next.city === city) {
      this.destinations = this.destinations.filter((d) => d.id !== next.id);
      this.promoteNext();
      this.publishPlannerEvent('destinationChanged', { city, country }, e.at);
      this.publishPlannerEvent('milestoneReached', { milestone: `Arrived in ${city}` }, e.at);
    } else {
      this.publishPlannerEvent('destinationChanged', { city, country }, e.at);
    }
    this.afterUpdate();
  }

  private onPaymentReceived(e: CoreEvent): void {
    const p = (e.payload ?? {}) as Record<string, unknown>;
    const amount = typeof p.amount === 'number' && Number.isFinite(p.amount) && p.amount > 0 ? p.amount : null;
    if (amount == null) return;
    this.progressGoals(['income', 'savings'], amount, e.at);
  }

  private onMissionCompleted(e: CoreEvent): void {
    this.progressGoals(['mission'], 1, e.at);
  }

  private progressGoals(types: PlannerGoalType[], delta: number, at: number): void {
    let changed = false;
    for (const goal of this.goals) {
      if (goal.completedAt != null || !types.includes(goal.type)) continue;
      goal.currentAmount += delta;
      changed = true;
      if (goal.currentAmount >= goal.targetAmount) {
        goal.completedAt = at;
        this.publishPlannerEvent('goalCompleted', { goalId: goal.id, title: goal.title, type: goal.type }, at);
      }
    }
    if (changed) this.afterUpdate();
  }

  // ── Travel plans ────────────────────────────────────────────────────────────

  getTravelPlan(): TravelPlanState {
    return structuredClone(this.travelState());
  }

  private travelState(): TravelPlanState {
    return {
      currentBaseCity: this.currentBaseCity,
      currentBaseCountry: this.currentBaseCountry,
      nextDestination: this.destinations.find((d) => d.status === 'next') ?? null,
      futureDestinations: this.destinations.filter((d) => d.status === 'future'),
      dreamDestinations: this.destinations.filter((d) => d.status === 'dream'),
    };
  }

  addDestination(input: NewDestinationInput, at: number = Date.now()): PlannedDestination | null {
    if (!asString(input.city) || !asString(input.country)) return null;
    if (input.plannedArrival != null && input.plannedDeparture != null &&
        input.plannedDeparture <= input.plannedArrival) return null;
    // Only one 'next' destination — a new one demotes the old to 'future'.
    if (input.status === 'next') {
      for (const d of this.destinations) {
        if (d.status === 'next') d.status = 'future';
      }
    }
    const destination: PlannedDestination = {
      id: `dest-${at}-${this.destinations.length}`,
      city: input.city.trim(),
      country: input.country.trim(),
      status: input.status,
      plannedArrival: input.plannedArrival ?? null,
      plannedDeparture: input.plannedDeparture ?? null,
      flexibleDates: input.flexibleDates === true || input.plannedArrival == null,
      notes: input.notes ?? '',
      addedAt: at,
    };
    this.destinations.push(destination);
    if (this.destinations.length > MAX_DESTINATIONS) this.destinations = this.destinations.slice(-MAX_DESTINATIONS);
    this.publishPlannerEvent('planCreated', { destinationId: destination.id, city: destination.city, status: destination.status }, at);
    if (destination.plannedArrival != null) {
      this.publishPlannerEvent('travelScheduled', {
        destinationId: destination.id, city: destination.city, plannedArrival: destination.plannedArrival,
      }, at);
    }
    this.afterUpdate();
    return structuredClone(destination);
  }

  updateDestination(id: string, patch: Partial<Omit<PlannedDestination, 'id' | 'addedAt'>>, at: number = Date.now()): boolean {
    const destination = this.destinations.find((d) => d.id === id);
    if (!destination) return false;
    // Reject patches that would produce an invalid date order.
    const nextArrival = 'plannedArrival' in patch ? patch.plannedArrival ?? null : destination.plannedArrival;
    const nextDeparture = 'plannedDeparture' in patch ? patch.plannedDeparture ?? null : destination.plannedDeparture;
    if (nextArrival != null && nextDeparture != null && nextDeparture <= nextArrival) return false;
    const hadArrival = destination.plannedArrival != null;
    Object.assign(destination, patch);
    if (patch.status === 'next') {
      for (const d of this.destinations) {
        if (d.id !== id && d.status === 'next') d.status = 'future';
      }
    }
    this.publishPlannerEvent('planUpdated', { destinationId: id }, at);
    if (!hadArrival && destination.plannedArrival != null) {
      this.publishPlannerEvent('travelScheduled', {
        destinationId: id, city: destination.city, plannedArrival: destination.plannedArrival,
      }, at);
    }
    this.afterUpdate();
    return true;
  }

  removeDestination(id: string, at: number = Date.now()): boolean {
    const before = this.destinations.length;
    this.destinations = this.destinations.filter((d) => d.id !== id);
    if (this.destinations.length === before) return false;
    this.publishPlannerEvent('planUpdated', { destinationId: id, removed: true }, at);
    this.afterUpdate();
    return true;
  }

  private promoteNext(): void {
    if (this.destinations.some((d) => d.status === 'next')) return;
    const soonest = this.destinations
      .filter((d) => d.status === 'future')
      .sort((a, b) => (a.plannedArrival ?? Number.MAX_SAFE_INTEGER) - (b.plannedArrival ?? Number.MAX_SAFE_INTEGER))[0];
    if (soonest) soonest.status = 'next';
  }

  // ── Lifestyle plan ──────────────────────────────────────────────────────────

  getLifestylePlan(): LifestylePlan {
    return structuredClone(this.lifestyle);
  }

  updateLifestylePlan(patch: Partial<LifestylePlan>, at: number = Date.now()): LifestylePlan {
    this.lifestyle = { ...this.lifestyle, ...patch };
    this.publishPlannerEvent('planUpdated', { section: 'lifestyle' }, at);
    this.afterUpdate();
    return this.getLifestylePlan();
  }

  // ── Goals ───────────────────────────────────────────────────────────────────

  addGoal(
    type: PlannerGoalType,
    title: string,
    targetAmount: number,
    targetDate: number | null = null,
    at: number = Date.now(),
  ): PlannerGoal | null {
    if (!PLANNER_GOAL_TYPES.includes(type)) return null;
    if (!asString(title) || !(targetAmount > 0)) return null;
    const goal: PlannerGoal = {
      id: `pgoal-${at}-${this.goals.length}`,
      type,
      title: title.trim(),
      targetAmount,
      currentAmount: 0,
      targetDate,
      createdAt: at,
      completedAt: null,
    };
    this.goals.push(goal);
    if (this.goals.length > MAX_GOALS) this.goals = this.goals.slice(-MAX_GOALS);
    this.publishPlannerEvent('planCreated', { goalId: goal.id, type, title: goal.title }, at);
    this.afterUpdate();
    return structuredClone(goal);
  }

  /** Manual progress for goals that no bus event can measure (travel/xp/country/city/personal). */
  progressGoal(id: string, delta: number, at: number = Date.now()): boolean {
    if (!(delta > 0)) return false;
    const goal = this.goals.find((g) => g.id === id);
    if (!goal || goal.completedAt != null) return false;
    goal.currentAmount += delta;
    if (goal.currentAmount >= goal.targetAmount) {
      goal.completedAt = at;
      this.publishPlannerEvent('goalCompleted', { goalId: goal.id, title: goal.title, type: goal.type }, at);
    }
    this.afterUpdate();
    return true;
  }

  getGoals(): PlannerGoal[] {
    return structuredClone(this.goals);
  }

  // ── Intelligence surfaces (computed at read time — always current) ─────────

  getSuggestions(now: number = Date.now()): PlannerSuggestion[] {
    return structuredClone(computeSuggestions({
      travel: this.travelState(),
      lifestyle: this.lifestyle,
      goals: this.goals,
      now,
    }));
  }

  getTimeline(now: number = Date.now()): PlannerTimeline {
    return structuredClone(computeTimeline({
      travel: this.travelState(),
      lifestyle: this.lifestyle,
      goals: this.goals,
      now,
    }));
  }

  answerQuestion(question: PlannerQuestion, now: number = Date.now()): DecisionSupport {
    return structuredClone(answerQuestion(question, {
      travel: this.travelState(),
      lifestyle: this.lifestyle,
      goals: this.goals,
      now,
    }));
  }

  // ── Compare futures ─────────────────────────────────────────────────────────

  addScenario(input: Omit<FutureScenario, 'id' | 'createdAt'>, at: number = Date.now()): FutureScenario | null {
    if (!asString(input.name)) return null;
    const scenario: FutureScenario = { ...input, id: `scenario-${at}-${this.scenarios.length}`, createdAt: at };
    this.scenarios.push(scenario);
    if (this.scenarios.length > MAX_SCENARIOS) this.scenarios = this.scenarios.slice(-MAX_SCENARIOS);
    this.afterUpdate();
    return structuredClone(scenario);
  }

  getScenarios(): FutureScenario[] {
    return structuredClone(this.scenarios);
  }

  compareFutures(scenarioIds: string[]): FutureComparison | null {
    const scenarios = scenarioIds
      .map((id) => this.scenarios.find((s) => s.id === id))
      .filter((s): s is FutureScenario => s != null);
    if (scenarios.length < 2) return null;
    const numericRow = (label: string, pick: (s: FutureScenario) => number | null, higherWins: boolean) => {
      const values = scenarios.map(pick);
      let winner: string | null = null;
      if (values.every((v) => v != null)) {
        const best = higherWins ? Math.max(...(values as number[])) : Math.min(...(values as number[]));
        const winners = scenarios.filter((_, i) => values[i] === best);
        winner = winners.length === 1 ? winners[0].name : null; // ties have no winner
      }
      return { row: { label, values: values as (number | null)[] }, edge: { label, winner } };
    };
    const noteRow = (label: string, pick: (s: FutureScenario) => string | null) => ({
      label,
      values: scenarios.map(pick),
    });
    const numeric = [
      numericRow('Monthly budget', (s) => s.monthlyBudget, false),
      numericRow('Roamer Score™', (s) => s.roamerScore, true),
      numericRow('Projected monthly savings', (s) => s.projectedMonthlySavings, true),
      numericRow('Income potential', (s) => s.incomePotential, true),
    ];
    return structuredClone({
      scenarioIds: scenarios.map((s) => s.id),
      names: scenarios.map((s) => s.name),
      rows: [
        noteRow('City', (s) => s.city),
        ...numeric.map((n) => n.row),
        noteRow('Housing', (s) => s.housingNote),
        noteRow('Community', (s) => s.communityNote),
        noteRow('Weather', (s) => s.weatherNote),
        noteRow('Quality of life', (s) => s.qualityOfLifeNote),
      ],
      edges: numeric.map((n) => n.edge),
    });
  }

  // ── Export / delete ─────────────────────────────────────────────────────────

  exportPlanner(now: number = Date.now()): PlannerExport {
    return structuredClone({
      exportedAt: now,
      travelPlan: this.travelState(),
      lifestylePlan: this.lifestyle,
      goals: this.goals,
      scenarios: this.scenarios,
    });
  }

  deleteAll(): void {
    this.currentBaseCity = null;
    this.currentBaseCountry = null;
    this.destinations = [];
    this.lifestyle = { ...DEFAULT_LIFESTYLE_PLAN };
    this.goals = [];
    this.scenarios = [];
    this.persist();
    this.emitChange();
  }

  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emitChange(): void {
    for (const l of this.listeners) {
      try {
        l();
      } catch { /* isolated */ }
    }
  }

  private afterUpdate(): void {
    this.persist();
    this.emitChange();
  }

  // ── Persistence ─────────────────────────────────────────────────────────────

  private persist(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const state: PersistedState = {
        currentBaseCity: this.currentBaseCity,
        currentBaseCountry: this.currentBaseCountry,
        destinations: this.destinations,
        lifestyle: this.lifestyle,
        goals: this.goals,
        scenarios: this.scenarios,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      storageManager.push('planner', 'state', state);
    } catch {
      /* storage unavailable — keep working in memory */
    }
  }

  /** Cloud hydration: reload in-memory state from localStorage after cloud adoption. */
  reloadFromStorage(now = Date.now()): void {
    this.load(now);
    this.emitChange();
  }

  private load(_now: number): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const state = JSON.parse(raw) as Record<string, unknown> | null;
      if (state == null || typeof state !== 'object') return;

      this.currentBaseCity = asString(state.currentBaseCity);
      this.currentBaseCountry = asString(state.currentBaseCountry);

      this.destinations = (Array.isArray(state.destinations) ? state.destinations : [])
        .filter((d): d is Record<string, unknown> =>
          d != null && typeof d === 'object' &&
          typeof (d as { id?: unknown }).id === 'string' &&
          typeof (d as { city?: unknown }).city === 'string' &&
          typeof (d as { country?: unknown }).country === 'string')
        .map((d): PlannedDestination => {
          let plannedArrival = asNumberOrNull(d.plannedArrival);
          let plannedDeparture = asNumberOrNull(d.plannedDeparture);
          // Invariant: departure must be after arrival — invalid pairs are nulled.
          if (plannedArrival != null && plannedDeparture != null && plannedDeparture <= plannedArrival) {
            plannedArrival = null;
            plannedDeparture = null;
          }
          return {
            id: d.id as string,
            city: d.city as string,
            country: d.country as string,
            status: d.status === 'next' || d.status === 'future' || d.status === 'dream' ? d.status : 'future',
            plannedArrival,
            plannedDeparture,
            flexibleDates: d.flexibleDates === true,
            notes: typeof d.notes === 'string' ? d.notes : '',
            addedAt: asNumberOrNull(d.addedAt) ?? 0,
          };
        })
        .slice(-MAX_DESTINATIONS);
      // Invariant: at most one 'next' destination survives a reload.
      let seenNext = false;
      for (const d of this.destinations) {
        if (d.status === 'next') {
          if (seenNext) d.status = 'future';
          seenNext = true;
        }
      }

      const l = (state.lifestyle ?? {}) as Record<string, unknown>;
      this.lifestyle = {
        whereToLive: asString(l.whereToLive),
        stayDurationMonths: asNumberOrNull(l.stayDurationMonths),
        monthlyBudget: asNumberOrNull(l.monthlyBudget),
        budgetCurrency: asString(l.budgetCurrency) ?? 'USD',
        monthlyIncomeGoal: asNumberOrNull(l.monthlyIncomeGoal),
        housingTimelineNote: asString(l.housingTimelineNote),
        visaTimelineNote: asString(l.visaTimelineNote),
        missionsPerWeek: asNumberOrNull(l.missionsPerWeek),
        coworkingDaysPerWeek: asNumberOrNull(l.coworkingDaysPerWeek),
        restDaysPerWeek: asNumberOrNull(l.restDaysPerWeek),
        explorationDaysPerWeek: asNumberOrNull(l.explorationDaysPerWeek),
      };

      this.goals = (Array.isArray(state.goals) ? state.goals : [])
        .filter((g): g is PlannerGoal =>
          g != null && typeof g === 'object' &&
          typeof (g as PlannerGoal).id === 'string' &&
          PLANNER_GOAL_TYPES.includes((g as PlannerGoal).type) &&
          typeof (g as PlannerGoal).title === 'string' &&
          typeof (g as PlannerGoal).targetAmount === 'number' &&
          (g as PlannerGoal).targetAmount > 0 &&
          typeof (g as PlannerGoal).currentAmount === 'number')
        .map((g) => ({
          ...g,
          targetDate: asNumberOrNull(g.targetDate),
          completedAt: asNumberOrNull(g.completedAt),
          createdAt: asNumberOrNull(g.createdAt) ?? 0,
        }))
        .slice(-MAX_GOALS);

      this.scenarios = (Array.isArray(state.scenarios) ? state.scenarios : [])
        .filter((s): s is Record<string, unknown> =>
          s != null && typeof s === 'object' &&
          typeof (s as { id?: unknown }).id === 'string' &&
          typeof (s as { name?: unknown }).name === 'string')
        .map((s): FutureScenario => ({
          id: s.id as string,
          name: s.name as string,
          city: asString(s.city),
          monthlyBudget: asNumberOrNull(s.monthlyBudget),
          roamerScore: asNumberOrNull(s.roamerScore),
          projectedMonthlySavings: asNumberOrNull(s.projectedMonthlySavings),
          incomePotential: asNumberOrNull(s.incomePotential),
          housingNote: asString(s.housingNote),
          communityNote: asString(s.communityNote),
          weatherNote: asString(s.weatherNote),
          qualityOfLifeNote: asString(s.qualityOfLifeNote),
          createdAt: asNumberOrNull(s.createdAt) ?? 0,
        }))
        .slice(-MAX_SCENARIOS);
    } catch {
      /* corrupt state — start fresh rather than crash */
    }
  }
}

/** App-wide singleton. Call attachToCore() during app bootstrap (after roamerCore.initialize()). */
export const roamerPlannerModule = new RoamerPlannerModule();

/**
 * Boot hydration: adopt cloud Planner state if strictly newer than local.
 * Called by HydrationOrchestrator. Returns true if cloud data was adopted.
 * If no cloud record exists yet, migrates current local data to cloud (first-run).
 */
export async function hydratePlannerFromCloud(): Promise<boolean> {
  const cloud = await storageManager.hydrate<PersistedState>('planner', 'state');
  if (!cloud) {
    // Migrate: push existing local data to cloud on first run with cloud sync.
    try {
      if (typeof localStorage !== 'undefined') {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) storageManager.push('planner', 'state', JSON.parse(raw) as PersistedState);
      }
    } catch { /* best-effort */ }
    return false;
  }
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cloud));
    }
    roamerPlannerModule.reloadFromStorage();
    return true;
  } catch { return false; }
}
